import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLanguage } from '@/lib/lang';
import { t } from '@/lib/i18n';

export function ProfileSampleImageBox({
  src,
  alt,
  wrapperClassName,
  imgClassName,
}: {
  src: string
  alt: string
  wrapperClassName: string
  imgClassName: string
}) {
  const { lang } = useLanguage()
  const label = t("profile_sample", lang)

  return (
    <figure className={wrapperClassName}>
      <img src={src} alt={alt} className={imgClassName} loading="lazy" />
      <figcaption className="absolute inset-x-0 top-0 p-2">
        <div className="w-full rounded-md bg-black/70 px-3 py-2 text-center text-lg font-extrabold leading-tight text-white shadow-lg backdrop-blur-sm sm:text-xl">
          {label}
        </div>
      </figcaption>
    </figure>
  )
}

export function AnchoredPopup({
  open,
  anchorRef,
  children,
}: {
  open: boolean
  anchorRef: React.RefObject<HTMLElement>
  children: React.ReactNode
}) {
  const popupRef = useRef<HTMLDivElement | null>(null)
  const [rendered, setRendered] = useState(open)
  const [pos, setPos] = useState<{ left: number; top: number; placement: "top" | "bottom"; maxWidth: number } | null>(null)

  useEffect(() => {
    if (open) {
      setRendered(true)
      return
    }
    const t = globalThis.setTimeout(() => setRendered(false), 260)
    return () => globalThis.clearTimeout(t)
  }, [open])

  const updatePos = useCallback(() => {
    const anchor = anchorRef.current
    if (!anchor) return

    const rect = anchor.getBoundingClientRect()
    const vw = globalThis.innerWidth || 0
    const vh = globalThis.innerHeight || 0
    const padding = 8
    const maxWidth = Math.max(120, Math.min(320, vw - padding * 2))

    const centerX = rect.left + rect.width / 2

    const popupEl = popupRef.current
    const popupRect = popupEl?.getBoundingClientRect()
    const popupWidth = popupRect?.width || Math.min(260, maxWidth)
    const popupHeight = popupRect?.height || 44

    const gap = 12
    const topFits = rect.top - gap - popupHeight >= padding
    const bottomFits = rect.bottom + gap + popupHeight <= vh - padding

    let placement: "top" | "bottom"
    if (topFits) placement = "top"
    else if (bottomFits) placement = "bottom"
    else placement = rect.top > vh / 2 ? "top" : "bottom"

    let top: number
    if (placement === "top") {
      const bottomY = rect.top - gap
      top = Math.max(padding + popupHeight, Math.min(bottomY, vh - padding))
    } else {
      const topY = rect.bottom + gap
      top = Math.min(Math.max(topY, padding), vh - padding - popupHeight)
    }
    const minLeft = padding + popupWidth / 2
    const maxLeft = vw - padding - popupWidth / 2
    const left = Math.min(Math.max(centerX, minLeft), Math.max(minLeft, maxLeft))

    setPos({ left, top, placement, maxWidth })
  }, [anchorRef])

  useLayoutEffect(() => {
    if (!rendered) return
    updatePos()
  }, [rendered, updatePos, children])

  useEffect(() => {
    if (!rendered) return
    const onMove = () => updatePos()
    globalThis.addEventListener("resize", onMove)
    globalThis.addEventListener("scroll", onMove, true)
    return () => {
      globalThis.removeEventListener("resize", onMove)
      globalThis.removeEventListener("scroll", onMove, true)
    }
  }, [rendered, updatePos])

  if (!rendered) return null

  return (
    <div
      ref={popupRef}
      role="alert"
      aria-live="polite"
      className={`fixed z-[9999] pointer-events-none transition-opacity duration-200 ${open ? "opacity-100" : "opacity-0"}`}
      style={{
        left: pos?.left ?? 0,
        top: pos?.top ?? 0,
        maxWidth: pos?.maxWidth ?? 320,
        transform: pos?.placement === "bottom" ? "translate(-50%, 0)" : "translate(-50%, -100%)",
      }}
    >
      {children}
    </div>
  )
}

interface EminentPersonDisplayProps {
  className?: string;
}

export const EminentPersonDisplay: React.FC<EminentPersonDisplayProps> = ({ className }) => {
  const { lang } = useLanguage();
  const [isMobileViewport, setIsMobileViewport] = useState(() => {
    if (typeof window === "undefined") return false
    return window.innerWidth <= 768
  })

  useEffect(() => {
    if (typeof window === "undefined") return
    const onResize = () => setIsMobileViewport(window.innerWidth <= 768)
    window.addEventListener("resize", onResize, { passive: true })
    return () => window.removeEventListener("resize", onResize)
  }, [])

  const title = t('cat_distinguished', lang)
  const titleLines = useCallback(() => {
    if (!isMobileViewport) return [title]
    if (lang === "en") {
      return ["Distinguished", "Eminent Citizen"]
    }
    const s = String(title || "").trim()
    if (!s) return [""]
    const idxs: number[] = []
    for (let i = 0; i < s.length; i += 1) {
      if (s[i] === " ") idxs.push(i)
    }
    if (!idxs.length) return [s]
    const mid = Math.floor(s.length / 2)
    let best = idxs[0]
    for (const i of idxs) {
      if (Math.abs(i - mid) < Math.abs(best - mid)) best = i
    }
    const a = s.slice(0, best).trim()
    const b = s.slice(best + 1).trim()
    if (!a || !b) return [s]
    return [a, b]
  }, [isMobileViewport, title])
  const lines = titleLines()

  return (
    <div className={`w-full my-6 ${className}`}>
      {/* 
        Banner Container 
        - Dark rich background (Deep Blue #0a2a66) with gradient
        - Gold borders for premium feel
        - Flexbox for alignment
      */}
      <div className="eminent-banner eminent-banner--distinguished relative flex items-center justify-center gap-4 md:gap-8 p-6 bg-gradient-to-r from-[#0a2a66] via-[#11337a] to-[#0a2a66] border-y-4 border-[#FFD700] shadow-xl rounded-lg overflow-hidden group">

        {/* Left Trophy Image */}
        <div className="eminent-banner__trophy flex-shrink-0 z-10 order-1">
          <img
            src="/images/gold.png"
            alt="Gold Trophy"
            className="eminent-banner__trophy-img w-16 h-16 md:w-20 md:h-20 object-contain drop-shadow-2xl transform group-hover:scale-110 transition-transform duration-500"
          />
        </div>

        {/* Title Text 
           - Gold (#FFD700)
           - H2 size (approx 1.5em / 24px-30px)
           - Bold
        */}
        <h2
          data-testid="distinguished-banner-title"
          className="eminent-banner__title z-10 order-2 text-fluid-h2 font-bold text-[#FFD700] text-center tracking-wider drop-shadow-[0_2px_2px_rgba(0,0,0,0.8)] font-serif mb-0"
        >
          {lines.length === 2 ? (
            <>
              <span className="block">{lines[0]}</span>
              <span className="block">{lines[1]}</span>
            </>
          ) : (
            lines[0]
          )}
        </h2>

        {/* Right Trophy Image */}
        <div className="eminent-banner__trophy flex-shrink-0 z-10 order-3">
          <img
            src="/images/gold.png"
            alt="Gold Trophy"
            className="eminent-banner__trophy-img w-16 h-16 md:w-20 md:h-20 object-contain drop-shadow-2xl transform group-hover:scale-110 transition-transform duration-500"
          />
        </div>

        {/* Subtle overlay/shine effect */}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/20 pointer-events-none"></div>
      </div>
    </div>
  );
};
